import React from 'react';

// Severity colour — same scale used by the heatmap (absolute deviation drives colour,
// sign only appears in the label). Green → yellow → orange → red as |deviation| grows.
function severityColor(abs, maxAbs) {
  const t = Math.min(abs / maxAbs, 1);
  if (t < 0.5) {
    const u = t * 2;
    // green → yellow
    const r = Math.round(56  + (255 - 56)  * u);
    const g = Math.round(212 + (193 - 212) * u);
    const b = Math.round(48  + (7   - 48)  * u);
    return { color: `rgb(${r},${g},${b})`, bg: `rgba(${r},${g},${b},0.13)` };
  } else {
    const u = (t - 0.5) * 2;
    // yellow → red
    const r = Math.round(255 + (220 - 255) * u);
    const g = Math.round(193 + (53  - 193) * u);
    const b = Math.round(7   + (69  - 7)   * u);
    return { color: `rgb(${r},${g},${b})`, bg: `rgba(${r},${g},${b},0.13)` };
  }
}

function PctBadge({ pct }) {
  if (pct == null) return <span style={{ color: '#6b7a8d' }}>—</span>;
  const { color, bg } = severityColor(Math.abs(pct), 10);
  const sign = pct >= 0 ? '+' : '';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 4,
      background: bg, color, fontWeight: 600,
      fontVariantNumeric: 'tabular-nums', fontSize: 13,
    }}>
      {sign}{pct.toFixed(1)}%
    </span>
  );
}

function SigmaBadge({ sigma }) {
  if (sigma == null) return <span style={{ color: '#6b7a8d' }}>—</span>;
  const abs = Math.abs(sigma);
  const sign = sigma >= 0 ? '+' : '';
  // Below 1σ keep it muted; above 1σ use the same severity ramp as the heatmap
  if (abs < 1) {
    return (
      <span style={{
        display: 'inline-block', padding: '2px 7px', borderRadius: 4,
        background: 'transparent', color: '#8892a0', fontWeight: 600,
        fontVariantNumeric: 'tabular-nums', fontSize: 13,
      }}>
        {sign}{sigma.toFixed(2)} σ
      </span>
    );
  }
  const { color, bg } = severityColor(abs, 3);
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 4,
      background: bg, color, fontWeight: 600,
      fontVariantNumeric: 'tabular-nums', fontSize: 13,
    }}>
      {sign}{sigma.toFixed(2)} σ
    </span>
  );
}

export default function StatsPanel({ segmentAnalysis, labels = {}, title = 'Cell Analysis', mode = 'voltage', rectifierCount = 0 }) {
  const data = segmentAnalysis || [];
  const isResistance = mode === 'resistance';

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="card-header">
        <span className="card-title">{title}</span>
      </div>

      {/* Warn when no rectifier columns found — downtime rows are included in averages */}
      {!isResistance && rectifierCount === 0 && data.length > 0 && (
        <div style={{ margin: '8px 16px 0', padding: '8px 12px', background: 'rgba(230,180,0,0.12)', borderRadius: 6, border: '1px solid rgba(230,180,0,0.35)', fontSize: 12, color: '#b8860b' }}>
          ⚠️ No rectifier columns detected — σ values include downtime rows and may be misleading. Check the column mapping for this project.
        </div>
      )}

      {data.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-text">
            {isResistance
              ? 'No resistance data — requires rectifier current (REC-AI) in the dataset'
              : 'No cell data available'}
          </div>
        </div>
      ) : (
        <div className="stats-scroll">
          <table className="stats-table">
            <thead>
              <tr>
                <th>Segment</th>
                <th style={{ textAlign: 'right' }}>Cell</th>
                <th style={{ textAlign: 'right' }}>{isResistance ? 'Cell Avg (mΩ)' : 'Cell Avg (V)'}</th>
                <th style={{ textAlign: 'right' }}>{isResistance ? 'Seg. Avg (mΩ)' : 'Seg. Avg (V)'}</th>
                <th style={{ textAlign: 'center' }}>σ from Mean</th>
                <th style={{ textAlign: 'center' }}>% from Mean</th>
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => {
                const cellVal = isResistance ? r.cell_resistance_avg : r.cell_voltage_avg;
                const segVal  = r.segment_avg;
                const isOff   = (r.off_pct ?? 0) >= 40;
                const offStyle = isOff ? { color: '#4a5a6a', fontStyle: 'italic' } : {};
                return (
                  <tr key={i} style={isOff ? { background: 'rgba(42,58,74,0.45)', opacity: 0.75 } : {}}>
                    <td style={{ color: isOff ? '#4a5a6a' : '#8892a0', fontSize: 12 }}>{labels[r.segment_key] || r.segment}</td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 600, ...offStyle }}>{r.cell}</td>
                    <td className="mono" style={{ textAlign: 'right', ...offStyle }}>
                      {isOff
                        ? <span title={`Rectifier OFF ${(r.off_pct ?? 0).toFixed(0)}% of the time`}>— <span style={{ fontSize: 10, color: '#4a5a6a' }}>OFF</span></span>
                        : (cellVal != null ? cellVal.toFixed(isResistance ? 3 : 3) : '—')}
                    </td>
                    <td className="mono" style={{ textAlign: 'right', color: isOff ? '#4a5a6a' : '#8892a0' }}>{segVal != null ? segVal.toFixed(isResistance ? 3 : 3) : '—'}</td>
                    <td style={{ textAlign: 'center' }}>{isOff ? <span style={{ color: '#4a5a6a', fontSize: 11 }}>—</span> : <SigmaBadge sigma={r.sigma_from_mean} />}</td>
                    <td style={{ textAlign: 'center' }}>{isOff ? <span style={{ color: '#4a5a6a', fontSize: 11 }}>—</span> : <PctBadge pct={r.pct_from_mean} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
